import React from 'react';
import { ConfirmModal, ButtonColors, Text, TextAs, TextSizes } from '@clockout-dev/design-elements';
import { USER_ACTIONS } from './user-actions.controller';
import { restrictCopy, ssoCopy } from './userAction.utils';

/**
 * The one confirmation dialog for Open SSO and Restrict access, rendered once
 * per page and driven entirely by the `userActionConfirm` slice.
 *
 * `onOk` always returns false so the modal never closes itself: the controller
 * closes it on success, and a failed request leaves it open to retry or cancel.
 * Same contract as ReconcileAccountModal.
 *
 * @param {Object} props
 * @param {{action: string, name?: string, submitting?: boolean}|null} props.confirm
 *   The open confirmation, or null when nothing is open.
 * @param {() => void} props.onCancel
 * @param {() => Promise<void>} props.onConfirm
 */
function UserActionConfirmModal({ confirm, onCancel, onConfirm }) {
    // Keep the last confirm so the copy does not blank out during the modal's
    // close animation, after the controller has already cleared the slice.
    const [shown, setShown] = React.useState(confirm);
    React.useEffect(() => {
        if (confirm) setShown(confirm);
    }, [confirm]);

    // Focused when the open animation finishes — see the modal below.
    const cancelRef = React.useRef(null);

    const active = confirm || shown;
    if (!active) return null;

    const isSso = active.action === USER_ACTIONS.SSO;
    const copy = isSso ? ssoCopy(active.name) : restrictCopy(active.name);
    const submitting = !!active.submitting;

    const handleOk = async () => {
        if (!submitting) await onConfirm();
        return false;
    };

    return (
        <ConfirmModal
            title={copy.title}
            open={!!confirm}
            onCancel={onCancel}
            onOk={handleOk}
            okText={copy.okText}
            cancelText="Cancel"
            okButtonColor={copy.danger ? ButtonColors.DANGER : ButtonColors.PRIMARY}
            // Cancel holds focus, not the action button. Both confirmations
            // guard something the admin cannot take back from here — a lockout
            // and a real session in a real member's account — so a stray Enter
            // must dismiss, never commit.
            //
            // Done on afterOpenChange rather than with autoFocus: the panel is
            // still display:none while rc-motion runs the open transition, so a
            // child's autoFocus focuses nothing (which is why the previous
            // okButtonProps={{ autoFocus: true }} was quietly a no-op). This
            // hook fires right after rc-dialog focuses the panel itself, so it
            // is also the last word on where focus lands.
            afterOpenChange={(isOpen) => {
                if (isOpen) cancelRef.current?.focus();
            }}
            cancelButtonProps={{ ref: cancelRef }}
            confirmLoading={submitting}
            destroyOnHidden
        >
            {copy.body ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {copy.body.map((paragraph) => (
                        <Text key={paragraph} as={TextAs.P} size={TextSizes.SMALL}>
                            {paragraph}
                        </Text>
                    ))}
                </div>
            ) : null}
        </ConfirmModal>
    );
}

export default UserActionConfirmModal;
